import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { MicSetupModal } from "./MicSetupModal";
import { useMicrophoneStatus } from "./useMicrophoneStatus";
import { useMicrophone } from "./useMicrophone";
import { useSound } from "./useSound";
import { SOUND_SPEAKER_TONE } from "../../systems/sound-effects-system";
import { useSpeakers } from "./useSpeakers";
import { useCallback } from "react";
import { useState } from "react";
import MediaDevicesManager from "../../utils/media-devices-manager";
import { useCan } from "./useCan";
import { useCameraDevices } from "./useCameraDevices";
import { useVolumeMeter } from "../misc/useVolumeMeter";
import configManager from "../../singleton/ConfigManager";
import { NO_DEVICE_ID, PermissionStatus } from "../../utils/media-devices-utils";

export function MicSetupModalContainer({ scene, enteredInAr, ...rest }) {
  const { isMicEnabled, permissionStatus, prepareMic, setupMicListener } = useMicrophoneStatus(scene);
  const { micDeviceChanged, micDevices } = useMicrophone(scene);
  const { videoDeviceChanged, videoDevices, fetchVideoDevices, requestingVideoPermission, permissionStatusVideo } =
    useCameraDevices();
  const canVoiceChat = useCan("voice_chat");
  const { speakerDeviceChanged, speakerDevices } = useSpeakers();
  const { playSound } = useSound({
    scene,
    sound: SOUND_SPEAKER_TONE
  });
  const [isMicMutedOnEntry, setIsMicMutedOnEntry] = useState(APP.store.state.preferences["muteMicOnEntry"]);
  const onChangeMicrophoneMuted = useCallback(({ target: { checked: muted } }) => {
    setIsMicMutedOnEntry(muted);
    APP.store.update({
      preferences: { muteMicOnEntry: muted }
    });
  }, []);

  useEffect(() => {
    if (configManager.config.spawnCamera?.enabled && enteredInAr) {
      fetchVideoDevices();
    }

    if (configManager.config.voiceChat.enabled) {
      setupMicListener();
    }
    // Permission setup is intentionally one-shot when the entry modal mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (configManager.config.spawnCamera?.enabled && permissionStatusVideo === PermissionStatus.GRANTED) {
      fetchVideoDevices();
    }
    // Camera device refresh is intentionally one-shot here; adding dependencies can loop permission requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [micChanges, setMicChanges] = useState(0);

  const updateMic = useCallback(level => {
    setMicChanges(level);
  }, []);

  useVolumeMeter({
    analyser: scene.systems["hubs-systems"].audioSystem.outboundAnalyser,
    update: updateMic
  });

  const cameraNotConfigured = useMemo(() => videoDevices.value === NO_DEVICE_ID, [videoDevices]);

  return (
    <MicSetupModal
      micChanges={micChanges}
      onPlaySound={playSound}
      isMicrophoneEnabled={isMicEnabled}
      isMicrophoneMuted={isMicMutedOnEntry}
      permissionStatus={permissionStatus}
      microphoneOptions={micDevices}
      speakerOptions={speakerDevices}
      videoOptions={videoDevices}
      onChangeVideo={videoDeviceChanged}
      requestingVideoPermission={requestingVideoPermission}
      permissionStatusVideo={permissionStatusVideo}
      onChangeMicrophone={micDeviceChanged}
      onPrepareMicrophone={prepareMic}
      onChangeSpeaker={speakerDeviceChanged}
      onChangeMicrophoneMuted={onChangeMicrophoneMuted}
      isAudioInputSelectAvailable={MediaDevicesManager.isAudioInputSelectEnabled}
      isAudioOutputSelectAvailable={MediaDevicesManager.isAudioOutputSelectEnabled}
      canVoiceChat={canVoiceChat}
      enteredInAr={enteredInAr}
      fetchVideoDevices={fetchVideoDevices}
      cameraNotConfigured={cameraNotConfigured}
      {...rest}
    />
  );
}

MicSetupModalContainer.propTypes = {
  scene: PropTypes.object.isRequired,
  enteredInAr: PropTypes.bool.isRequired,
  isJoiningVerse: PropTypes.bool
};
